import React, { useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useAdminStore } from '../../stores/useAdminStore';
import { useSeasonsStore } from '../../stores/useSeasonsStore';
import { useUIStore } from '../../stores/useUIStore';
import { SEASON_NAMES } from '../../utils/seasonDomain';
import Button from '../ui/Button/Button';
import { BaseModal } from '../shared/Modal/BaseModal';
import './AdminFormControl.css';
import './AddSeasonModal.css';

const [WINTER_SEASON_NAME] = SEASON_NAMES;
// Creating Fall rolls the suggested next season (Winter) into the following year.
const SEASON_BEFORE_YEAR_ROLLOVER = 'Fall';
const YEAR_OPTIONS_BEFORE_CURRENT = 2;
const YEAR_OPTION_COUNT = 11;

export const SeasonCreateModal: React.FC = () => {
  const { showSeasonCreateModal, closeSeasonCreateModal } = useAdminStore(
    useShallow(s => ({
      showSeasonCreateModal: s.showSeasonCreateModal,
      closeSeasonCreateModal: s.closeSeasonCreateModal,
    }))
  );
  const { createSeason, fetchSeasons, isLoading, seasonExists } = useSeasonsStore();
  const { showToast } = useUIStore();

  const [newSeasonName, setNewSeasonName] = useState<string>(WINTER_SEASON_NAME);
  const [newSeasonYear, setNewSeasonYear] = useState(new Date().getFullYear());

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (seasonExists(newSeasonName, newSeasonYear)) {
      showToast(`${newSeasonName} ${newSeasonYear} already exists`, 'error');
      return;
    }

    try {
      await createSeason(newSeasonName, newSeasonYear);

      showToast(`${newSeasonName} ${newSeasonYear} created successfully`, 'success');
      closeSeasonCreateModal();

      const nextYear = newSeasonName === SEASON_BEFORE_YEAR_ROLLOVER ? newSeasonYear + 1 : newSeasonYear;
      const nextSeason = getNextSeason(newSeasonName);
      setNewSeasonName(nextSeason);
      setNewSeasonYear(nextYear);

    } catch (error) {
      // If creation fails due to race condition, refresh seasons and show appropriate error
      await fetchSeasons();
      if (seasonExists(newSeasonName, newSeasonYear)) {
        showToast(`${newSeasonName} ${newSeasonYear} was just created by another action`, 'info');
      } else {
        showToast((error as Error).message, 'error');
      }
    }
  };

  const getNextSeason = (currentSeason: string): string => {
    const currentIndex = SEASON_NAMES.indexOf(currentSeason as typeof SEASON_NAMES[number]);
    return SEASON_NAMES[(currentIndex + 1) % SEASON_NAMES.length];
  };

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: YEAR_OPTION_COUNT }, (_, i) => currentYear - YEAR_OPTIONS_BEFORE_CURRENT + i);

  return (
    <BaseModal
      isOpen={showSeasonCreateModal}
      onClose={closeSeasonCreateModal}
      title="Create New Season"
      className="season-modal-content"
      data-testid="season-create-modal"
    >
      <form onSubmit={handleSubmit} className="season-create-form">
        <div className="form-group">
          <label htmlFor="season-name">Season:</label>
          <select
            id="season-name"
            value={newSeasonName}
            onChange={(e) => setNewSeasonName(e.target.value)}
            className="admin-form-control admin-form-control--focus-ring season-select"
            required
            data-testid="season-name-input"
          >
            {SEASON_NAMES.map(season => (
              <option key={season} value={season}>{season}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="season-year">Year:</label>
          <select
            id="season-year"
            value={newSeasonYear}
            onChange={(e) => setNewSeasonYear(parseInt(e.target.value))}
            className="admin-form-control admin-form-control--focus-ring season-select"
            required
            data-testid="season-year-input"
          >
            {yearOptions.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>

        <div className="form-actions">
          <Button size="action"
            type="button"
            onClick={closeSeasonCreateModal}
            variant="secondary"
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button size="action"
            type="submit"
            disabled={isLoading}
            variant="primary"
            data-testid="create-season-button"
          >
            {isLoading ? 'Creating...' : 'Create Season'}
          </Button>
        </div>
      </form>
    </BaseModal>
  );
};
